<template>
    <div class="position-relative evidence-checker-container">
        <div class="row">
            <div v-if="showDescription" :class="smallLicensePlate ? 'col-lg-6 col-md-12' : 'col-md-12'">
                <p class="text-muted" v-html="$t('evidence.description')"></p>
            </div>

            <div v-if="showLandingPageDescription" :class="smallLicensePlate ? 'col-lg-6 col-md-12' : 'col-md-12'">
                <p class="text-muted" v-html="$t('evidence.landing_page_description')"></p>
            </div>

            <form
                ref="evidenceForm"
                class="form"
                method="post"
                action=""
                @submit="onSubmit($event)"
                :class="{
                    'contains-license-plate-template': hasTemplateForm,
                    'col-lg-6 col-md-12': smallLicensePlate,
                    'col-md-12': !smallLicensePlate,
                }"
            >
                <div class="row">
                    <div class="mb-3 col-12 col-sm-12">
                        <label for="evidenceCountry" class="col-form-label">{{ $t('shop.licensing_state') }}</label>
                        <select
                            id="evidenceCountry"
                            class="form-control height-auto-override"
                            name="country_code"
                            :class="{ 'is-invalid': formErrors.country_code }"
                            v-model="licensePlateCountry"
                            @change="inputChange('country_code')"
                            required
                        >
                            <option :value="false" disabled>{{ $t('shop.dropdown_required') }}</option>
                            <option v-for="option in licensePlateCountries" :key="option.country_code" :value="option.country_code">
                                {{ $t('country.' + option.country_code) }}
                            </option>
                        </select>
                        <div class="invalid-feedback">
                            <span class="d-block" v-for="error in formErrors.country_code" :key="error">{{ error }}</span>
                        </div>
                    </div>

                    <div v-if="selectedLicensePlateCountry !== null" class="mb-3 col-12 col-sm-12">
                        <label for="licensePlate" class="col-form-label">{{ $t('shop.license_plate') }}</label>
                        <license-plate-input
                            :license-plate-country="selectedLicensePlateCountry"
                            :template="selectedLicensePlateCountry.template"
                            :required="true"
                            :is-invalid="typeof formErrors.license === 'object' && formErrors.license.length > 0"
                            name="license"
                            id="licensePlate"
                            @input="inputChange('license')"
                            @has-license-plate-template="hasTemplateForm = $event"
                            v-model="licensePlate.value"
                            :update="licensePlateUpdated"
                            :special-license-plate-error="formErrors.special_license_plate_confirmed || null"
                            @model-updated="licensePlateUpdated = false"
                        >
                            <span class="d-block" v-for="error in formErrors.license" :key="error">{{ error }}</span>
                        </license-plate-input>
                    </div>

                    <div class="col-12 mt-1">
                        <button
                            type="submit"
                            class="btn btn-primary btn-block"
                            :class="smallLicensePlate ? 'mt-1' : ''"
                            :disabled="queue.isLoading"
                        >
                            {{ $t('evidence.submit') }}
                        </button>
                    </div>
                </div>
            </form>
            <!-- /EVIDENCE_FORM_CONTAINER -->

            <div
                v-if="queue.failed && queue.reason !== null"
                :class="{ 'col-lg-6 col-md-12': smallLicensePlate, 'col-md-12': !smallLicensePlate }"
            >
                <div class="alert alert-warning mt-3">
                    <i class="mdi mdi-alert-circle"></i> <span v-html="queue.reason"></span>
                </div>
            </div>
            <div
                v-else-if="queue.results !== null && !queue.isLoading"
                :class="{ 'col-lg-6 col-md-12': smallLicensePlate, 'col-md-12': !smallLicensePlate }"
            >
                <div class="row">
                    <div v-if="!queue.failed && queue.results.length === 0" class="col-12 mt-3">
                        <div class="alert alert-dark">
                            <i class="mdi mdi-alert-circle"></i> <span v-html="$t('evidence.no_result')"></span>
                        </div>
                    </div>
                    <div v-else-if="queue.failed" class="col-12 mt-3">
                        <div class="alert alert-danger">
                            <i class="mdi mdi-alert-circle"></i> <span v-html="$t('generic.failed')"></span>
                        </div>
                    </div>

                    <div v-else v-for="result in queue.results" :key="JSON.stringify(result)" class="col-12 mt-3">
                        <div class="key-feature d-flex p-3 rounded bg-white shadow">
                            <div class="icon text-center rounded-pill mt-2 mr-3">
                                <i class="text-primary" :class="resultIcon(result)"></i>
                            </div>
                            <div class="content mt-2">
                                <h4 class="title mb-0">{{ resultTitle(result) }}</h4>
                                <span v-if="typeof result.isFlexRouteToll === 'boolean' && result.isFlexRouteToll">
                                    <small
                                        class="designation text-muted"
                                        v-html="$t('evidence.flexRouteTollValidity')"
                                    ></small>
                                </span>
                                <span v-else>
                                    <small
                                        v-if="isAlreadyValid(result)"
                                        class="designation text-muted"
                                        v-html="
                                            $t('evidence.already_valid', {
                                                until: result.until.formatted,
                                            })
                                        "
                                    ></small>
                                    <small
                                        v-else
                                        class="designation text-muted"
                                        v-html="
                                            $t('evidence.validity_range', {
                                                from: result.from.formatted,
                                                until: result.until.formatted,
                                            })
                                        "
                                    ></small>
                                </span>
                            </div>
                        </div>
                    </div>

                    <div class="col-12 mt-3">
                        <a :href="'/' + this.$language + '/shop'" class="btn btn-primary d-block">
                            <i class="mdi mdi-chevron-right"></i> {{ $t('frontend.d_vignette_buy') }}
                        </a>
                        <a
                            v-if="typeof hideRouteTollProducts !== 'boolean' || !hideRouteTollProducts"
                            :href="'/' + this.$language + '/shop/' + routeTollUrls[this.$language]"
                            class="btn btn-outline-primary d-block mt-3"
                        >
                            <i class="mdi mdi-chevron-right"></i> {{ $t('frontend.d_streckenmaut_buy') }}
                        </a>
                    </div>
                </div>
            </div>
        </div>

        <loading-animation
            :visible="queue.isLoading"
            :always-show="false"
            preloader-container-id=""
            status-container-id=""
            :loading-text="$t('evidence.querying')"
        ></loading-animation>
    </div>
</template>

<script>
import LicensePlateInput from './../LicensePlateInput.vue';
import LoadingAnimation from './../LoadingAnimation.vue';
import debounce from 'lodash/debounce';

export default {
    components: {
        LicensePlateInput,
        LoadingAnimation,
    },

    props: {
        showDescription: {
            type: Boolean,
            required: false,
            default() {
                return false;
            },
        },
        smallLicensePlate: {
            type: Boolean,
            required: false,
            default() {
                return false;
            },
        },
        showLandingPageDescription: {
            type: Boolean,
            required: false,
            default() {
                return false;
            },
        },
        showOrderButtons: {
            type: Boolean,
            required: false,
            default() {
                return true;
            },
        },
        licensePlateCountries: {
            type: Array,
            required: true,
            default() {
                return [
                    {
                        country_name: '',
                        country_code: '',
                        allows_multilicenseplate: false,
                        image: null,
                        placeholder: 'B-AB1234',
                    },
                ];
            },
        },
        routeTollUrls: {
            type: Object,
            required: false,
            default() {
                const urls = {};
                urls[this.$language] = '';

                return urls;
            },
        },
        defaultLicensePlateCountry: {
            type: String,
            required: false,
            default() {
                return '';
            },
        },
        defaultLicensePlate: {
            type: String,
            required: false,
            default() {
                return '';
            },
        },
        hideRouteTollProducts: {
            type: Boolean,
            required: false,
            default() {
                return false;
            },
        },
    },

    data() {
        return {
            licensePlateCountry: false,
            licensePlate: {
                value: '',
                confirm: '',
            },
            queue: {
                isLoading: false,
                failed: false,
                reason: null,
                id: 0,
                results: null,
                checkInterval: -1,
                isValid: false,
            },
            changedInputs: {
                country_code: false,
                license: false,
                license_confirm: false,
                special_license_plate_confirmed: true,
            },
            formErrors: {},
            debouncedValidation: null,
            hasTemplateForm: false,
            licensePlateUpdated: false,
        };
    },

    watch: {
        defaultLicensePlate() {
            this.licensePlate.value = this.defaultLicensePlate;
            this.licensePlate.confirm = this.defaultLicensePlate;
            this.changedInputs.license = true;
            this.changedInputs.license_confirm = true;
            this.licensePlateUpdated = true;
        },

        defaultLicensePlateCountry() {
            this.licensePlateCountry = this.defaultLicensePlateCountry;
            this.changedInputs.country_code = true;
        },
    },

    computed: {
        selectedLicensePlateCountry() {
            const result = this.licensePlateCountries.filter(
                (country) => country.country_code === this.licensePlateCountry
            );

            if (!result.length) {
                return null;
            }

            return result[0];
        },

        licensePlateBackground() {
            const selected = this.selectedLicensePlateCountry;

            if (selected === null || typeof selected.image !== 'string' || !selected.image.length) {
                return null;
            }

            return {
                'background-image': 'url(\'/' + selected.image + '\')',
            };
        },
    },

    methods: {
        /**
         * Handle the submit event handle, to start the checking process.
         *
         * @param $event
         *
         * @return {void}
         */
        onSubmit($event) {
            // Prevent the default event, to prevent an default submit of form
            $event.preventDefault();

            // In the case that an request is currently already ongoing, we should
            // prevent another submit of this form.
            if (this.queue.isLoading) {
                return;
            }

            // We should indicate an loading of data and prevent any resubmit.
            // While we validate the form and wait on an response.
            this.queue.isLoading = true;
            this.queue.reason = null;
            this.queue.failed = false;

            this.validateInput().then(() => {
                // In the case that the form is not valid, we can cancel the execution.
                // And wait for an next try.
                if (!this.queue.isValid) {
                    this.queue.isLoading = false;

                    return;
                }

                // Now send the request to handle the creation of an evidence item, in
                // the case that the request were successfully completed, we should create
                // a interval to check the status of the job.
                axios
                    .post('/' + this.$language + '/evidence', new FormData(this.$refs.evidenceForm))
                    .then((response) => {
                        if (!response.data.success || typeof response.data.id !== 'number' || isNaN(response.data.id)) {
                            // In the case the response contains a specialized reason for failure,
                            // we should display this.
                            if (typeof response.data.reason === 'string' && response.data.reason.length) {
                                this.queue.failed = true;
                                this.queue.reason = response.data.reason;
                            }

                            this.queue.isLoading = false;

                            return;
                        }

                        // Firstly we have to clear the interval if one exists
                        this.resetCheckingInterval();

                        this.queue.id = response.data.id;
                        this.queue.checkInterval = setInterval(() => {
                            this.checkJobStatus();
                        }, 3000);
                    })
                    .catch(() => {
                        this.queue.isLoading = false;
                    });
            });
        },

        /**
         * Handle the validation of evidence form to assure the final request will be working correctly.
         *
         * @returns {Promise<*>}
         */
        async validateInput() {
            return axios
                .post('/' + this.$language + '/backend/validate/evidence', new FormData(this.$refs.evidenceForm))
                .then((result) => {
                    // The given form is not valid to start an evidence check
                    if (!result.data.valid) {
                        // Before we apply the errors, we should filter the empty fields,
                        // from response. To prevent an error message on not entered inputs.
                        for (const key in result.data.errors) {
                            if (typeof this.changedInputs[key] === 'undefined' || !this.changedInputs[key]) {
                                delete result.data.errors[key];
                            }
                        }

                        this.formErrors = result.data.errors;
                        this.queue.isValid = false;

                        return false;
                    }

                    this.formErrors = {};
                    this.queue.isValid = true;

                    return true;
                })
                .catch(() => {
                    this.queue.isValid = false;
                });
        },

        /**
         * Handle the change of an input, to optional send an request to validate the form data.
         *
         * @param key
         * @param validateRequest
         *
         * @returns {Promise<void>}
         */
        async inputChange(key, validateRequest) {
            // We have an input key to update, than we should apply this to our
            // list of known inputs to validate.
            if (typeof key !== 'undefined') {
                this.changedInputs[key] = true;
            }

            // In the case that we should validate the input, we should send
            // the async request to handle this validation.
            if (typeof validateRequest === 'boolean' && validateRequest) {
                this.debouncedValidation();
            }
        },

        /**
         * Handle the check of evidence job status, to provide the result in this view.
         *
         * @return {*}
         */
        checkJobStatus() {
            return axios
                .get('/' + this.$language + '/evidence/status/' + this.queue.id)
                .then((response) => {
                    // In the case that the job is not completed yet, we should
                    // wait until the process were completed.
                    if (!response.data.completed) {
                        return;
                    }

                    // Firstly we have to clear the interval if one exists
                    this.resetCheckingInterval();

                    // Apply the results and end the loading animation.
                    this.queue.results = response.data.results;
                    this.queue.failed = response.data.failed;
                    this.queue.isLoading = false;
                })
                .catch(() => {
                    this.resetCheckingInterval();
                    this.queue.isLoading = false;
                });
        },

        /**
         * Handle the reset of the checking interval if an interval is active.
         *
         * @returns {this}
         */
        resetCheckingInterval() {
            if (this.queue.checkInterval !== -1) {
                clearInterval(this.queue.checkInterval);
                this.queue.checkInterval = -1;
            }

            return this;
        },

        /**
         * Determine the icon to use for displaying the result.
         *
         * @param item
         *
         * @returns {string}
         */
        resultIcon(item) {
            switch (item.type.vehicle) {
                case 'car':
                    return 'mdi mdi-car';

                case 'motorbike':
                    return 'mdi mdi-motorbike';

                default:
                    return 'mdi mdi-ticket-confirmation';
            }
        },

        /**
         * Determine the title to use for result item.
         *
         * @param item
         *
         * @returns {VueI18n.TranslateResult|*}
         */
        resultTitle(item) {
            switch (item.type.product) {
                case 'vignette':
                    return this.$t('evidence.vignette_' + item.type.vehicle);

                case 'routeToll':
                    if (typeof item.type.label === 'string' && item.type.label.length) {
                        return item.type.label;
                    }

                    if (typeof item.isFlexRouteToll === 'boolean' && item.isFlexRouteToll) {
                        return this.$t('evidence.route_toll_flex');
                    }

                    return this.$t('evidence.route_toll');

                default:
                    return item.title;
            }
        },

        /**
         * Determine if the result item is already valid for use.
         *
         * @param item
         *
         * @returns {boolean}
         */
        isAlreadyValid(item) {
            const date = new Date(item.from.value),
                now = new Date();

            date.setHours(0, 0, 0, 0);
            now.setHours(0, 0, 0, 0);

            return date <= now;
        },
    },

    created() {
        this.debouncedValidation = debounce(this.validateInput, 2000);
    },

    mounted() {
        if (this.defaultLicensePlateCountry) {
            this.licensePlateCountry = this.defaultLicensePlateCountry;
            this.changedInputs.country_code = true;
        }

        if (this.defaultLicensePlate) {
            this.licensePlate.value = this.defaultLicensePlate;
            this.licensePlate.confirm = this.defaultLicensePlate;
            this.changedInputs.license = true;
            this.changedInputs.license_confirm = true;
        }
    },
};
</script>
<style lang="scss">
.evidence-checker-container {
    .license-plate-background {
        width: 1rem;
        height: 1.5rem;

        background-repeat: no-repeat;
        background-size: contain;
        background-position: top center;
    }

    .loader-container {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        z-index: 9999999;

        background: rgba(240, 248, 247, 0.8);
    }
    .loader-container .preloader .status {
        .spinner {
            position: relative;
            text-align: center;

            .spinner-inner {
                position: relative;
                margin: 6.25rem auto 1rem;

                .double-bounce1,
                .double-bounce2 {
                    position: absolute;
                    top: 0;
                    left: 0;
                    width: 100%;
                    height: 100%;
                    border-radius: 50%;

                    background-color: #088673;
                    opacity: 0.6;

                    -webkit-animation: sk-bounce 2s infinite ease-in-out;
                    animation: sk-bounce 2s infinite ease-in-out;
                }

                .double-bounce2 {
                    -webkit-animation-delay: -1s;
                    animation-delay: -1s;
                }

                width: 2.5rem;
                height: 2.5rem;
            }

            .spinner-text {
                font-weight: bolder;
            }
        }

        position: absolute;
        top: 50%;
        left: 0;
        right: 0;
        -webkit-transform: translateY(-50%);
        transform: translateY(-50%);
    }
}
</style>
